import { EventEmitter, Injectable } from '@angular/core';
import { User } from '../models/user';
import { environment } from 'src/environments/environment';
import { BaseService } from './base.service';
import { BehaviorSubject, catchError, throwError } from 'rxjs';
import { ApplicationRole } from '../models/ApplicationRole';
import { ErrorCode } from 'src/app/constants/constants';

@Injectable({
  providedIn: 'root'
})
/**
 * Service to handle user-related operations.
 */
export class UserService {  
  latApiBaseUri: string = environment.apiUrl
  authToken: any;
  UserData: User = new User();
 
  private currentUserSubject = new BehaviorSubject<User>(new User());
  currentUser = this.currentUserSubject.asObservable();
  // EventEmitter to notify when the current user is updated
  userUpdated = new EventEmitter<User>();
  apiInvokeError = new EventEmitter<string>();
  constructor(private baseService: BaseService) { }
  /**
   * Get the user's claims.
   * @returns An observable of the user's claims.
   */
  getUserInfo() {
    return this.baseService.get<User>('Auth/GetUserClaims').pipe(
      catchError((error) => {
        // Emit the error event with a message
        
        if (error.statusText == ErrorCode.SERVER_NOT_AVAILABLE) {
          this.apiInvokeError.emit(ErrorCode.SERVER_NOT_AVAILABLE);
        }
        else {
          this.apiInvokeError.emit('Failed to fetch user info: ' + error.message);
        }
        return throwError(() => new Error(error.message)); // Updated throwError usage
      })
    );
  }
  getImpersonateUserInfo(email: any){
    return this.baseService.get<User>('Auth/GetImpersonateUserClaims?email=' + email);
  }
  getUserInfoFromGraph(email: any) {
    return this.baseService.get<User>('GraphClaims/Get?email=' + email);
  }
  getDemoApi(){
    return this.baseService.get<User>('demoapi/get');
  }
  
  getUserByCAI(userCAI: string) {
    return this.baseService.get<User>('/User/GetUserByCAI?userCAI=' + userCAI);
  }

  getUserByEmail(userEmail: string){
    return this.baseService.get<User>('User/GetUserByEmail?userEmail=' + userEmail);
  }

  GetUserDetails(searchText: string){
    return this.baseService.get<any>('User/GetUserDetails?userSearch=' + searchText);
  }
  
  setUserData(Userdata: User): void {
    this.UserData = Userdata;
  }
  getUserData(): User {
    return this.UserData;
  }
  setCurrentUser(user: User) {    
    this.currentUserSubject.next(user);
    this.userUpdated.emit(user); // Emit the event when the user is updated
  }

  /**
   * Checks if the current user has the specified roles assigned.
   *
   * @param roles - An array of roles to check against the current user's assigned roles.
   * @param matchAll - A boolean indicating whether all roles must match (`true`) 
   *                   or if at least one role must match (`false`). Defaults to `true`.
   * @returns `true` if the roles match the criteria; otherwise, `false`.
   *
   * @remarks
   * - If the current user or their `ApplicationRoles` are not defined, the method returns `false`.
   * - When `matchAll` is `true`, all roles in the `roles` array must be present in the user's `ApplicationRoles`.
   * - When `matchAll` is `false`, at least one role in the `roles` array must be present in the user's `ApplicationRoles`.
   */
  CheckUserRolesAssigned(roles: ApplicationRole[], matchAll: boolean = true): boolean {
    const currentUser = this.currentUserSubject.value;
    if (!currentUser || !currentUser.ApplicationRoles) {
      return false;
    }
    if (matchAll) {
      return roles.every(role => (currentUser.ApplicationRoles ?? []).includes(role));
    } else {
      return roles.some(role => (currentUser.ApplicationRoles ?? []).includes(role));
    }
  }  
  
  getCurrentUser(): User {
    if (this.currentUserSubject.value == null) {
      this.getUserInfo().subscribe((data: any) => { 
        this.setCurrentUser(data);
      });
    }
    return this.currentUserSubject.value;
  }
  getCurrentUserAsync(): Promise<User> {
    return new Promise((resolve, reject) => {      
      if (this.currentUserSubject.value == null || this.currentUserSubject.value?.User_Code == null) {        
        this.baseService.get<User>('Auth/GetUserClaims').subscribe({
          next: (data: any) => {            
            var currentUser = data.user;
            currentUser.IsRFCAdmin = data.ApplicationRoles?.indexOf(ApplicationRole.RFCAdmin) !== -1;
            this.setCurrentUser(currentUser);
            resolve(currentUser); // Resolve the promise with the user data
          },
          error: (error: any) => {
            reject(error); // Reject the promise if an error occurs
          },
        });
      } else {        
        resolve(this.currentUserSubject.value); // Resolve immediately if the user is already set
      }
    });
  }
  

}

